import React, { useEffect, useState } from 'react';
import { _dataProvider, IUser } from '../dataProvider/DataProvider';
import { userAvatarName } from './UserAvatarName';
import { Dialog } from '@fluentui/react/lib/Dialog';
import { DefaultButton, IButtonStyles, Spinner } from '@fluentui/react';
import i18n from '../i18n';
import { getIdigestChatHostConfig, type VineProfileState, type VineProfileUser } from '../chatHost';

export interface IUserProfileDialogProps {
  user: IUser | undefined;
  dismiss: any;
  launchOneOnOneWithFriend: any;
  hideUniqueId?: boolean;
}

export interface IUserProfileProps {
  user: IUser | undefined;
  updateFrameStyle?: any;
  launchOneOnOneWithFriend?: any;
  onDismiss?: () => void;
  hideUniqueId?: boolean;
}

function toVineProfileUser(user: IUser): VineProfileUser {
  const userId = Number(user.userId || user.id);
  return {
    userId,
    uniqueId: user.uniqueId || user.loginId,
    displayName: user.displayName || user.name || ''
  };
}

function VineProfileActions({ user, onDismiss }: { user: IUser; onDismiss?: () => void }) {
  const host = getIdigestChatHostConfig();
  const labels = host.vineProfileLabels ?? {
    message: 'Message',
    inviteToVine: 'Invite to Vine',
    connect: 'Connect on Vine',
    loading: 'Loading...'
  };
  const [state, setState] = useState<VineProfileState | 'loading'>('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    host
      .resolveVineProfileState?.(toVineProfileUser(user))
      .then((nextState) => {
        if (!cancelled) {
          setState(nextState);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState('not_connected');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, host]);

  const runAction = async (action?: (profileUser: VineProfileUser) => void | Promise<void>) => {
    if (!action || busy) {
      return;
    }

    setBusy(true);
    try {
      await action(toVineProfileUser(user));
      onDismiss?.();
    } finally {
      setBusy(false);
    }
  };

  const buttonStyles: Partial<IButtonStyles> = {
    root: {
      width: '200px !important',
      height: '36px !important',
      border: '1px solid #BBBBBB !important',
      borderRadius: '18px !important',
      padding: '0px !important',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1) !important'
    }
  };

  if (state === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
        <Spinner label={labels.loading} />
      </div>
    );
  }

  let label = labels.connect;
  let action = host.onVineConnect;

  if (state === 'connected') {
    label = labels.message;
    action = host.onVineMessage;
  } else if (state === 'not_on_vine') {
    label = labels.inviteToVine;
    action = host.onVineInvite;
  }

  return (
    <div
      className='button-container'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px'
      }}
    >
      <DefaultButton styles={buttonStyles} disabled={busy || !action} onClick={() => runAction(action)}>
        {label}
      </DefaultButton>
    </div>
  );
}

export const UserProfile: React.FunctionComponent<IUserProfileProps> = ({
  user,
  updateFrameStyle,
  launchOneOnOneWithFriend,
  onDismiss,
  hideUniqueId
}) => {
  const host = getIdigestChatHostConfig();
  const isVineProfile = host.profileMode === 'vine';
  const [block, setBlock] = useState(user?.block);
  const [tag, setTag] = useState(() => (user ? _dataProvider.getMyTagForUser(user.id || user.userId) : ''));
  const shareEnabled = false;

  const updateTag = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setTag(ev.target.value);
  };

  useEffect(() => {
    if (!user || isVineProfile) {
      return;
    }
    if (block !== user.block || tag !== _dataProvider.getMyTagForUser(user.id || user.userId)) {
      void handleTagAndBlockUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirror prior deps; tag/block drives silent contact update
  }, [block, tag, isVineProfile, user]);

  // Silent update
  const handleTagAndBlockUpdate = async () => {
    if (!user) {
      return;
    }
    await _dataProvider.updateContactForUser(user.id || user.userId, tag, Number(!!block));
  };

  const handleCommunicate = () => {
    if (!user) {
      return;
    }
    if (onDismiss) {
      onDismiss();
    }
    if (launchOneOnOneWithFriend) {
      launchOneOnOneWithFriend(user.userId || user.id, '', user.uniqueId || user.loginId);
    }
  };

  const handleShare = () => {
    // Share functionality to be implemented
    console.log('Share functionality not implemented yet');
  };

  const buttonStyles: Partial<IButtonStyles> = {
    root: {
      width: '200px !important',
      height: '36px !important',
      border: '1px solid #BBBBBB !important',
      borderRadius: '18px !important',
      padding: '0px !important',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1) !important'
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      <div className='selected-user-container'>
        <br />
        {userAvatarName(user, 2, !hideUniqueId, undefined, undefined, undefined, true)}
      </div>
      <div style={updateFrameStyle}>
        {!isVineProfile && (
          <>
            <div className='avatar-name'>
              <h3>{i18n.t('MemberScreen.UserTag')}</h3>
              <input
                key='titleText'
                placeholder={tag ? tag : i18n.t('MemberScreen.NoTag')}
                value={tag}
                onChange={updateTag}
              />
            </div>
            <div className='avatar-name'>
              <h3>
                <input type={'checkbox'} checked={block} onChange={() => setBlock(!block)} />
                <span>{i18n.t('MemberScreen.BlockUser')}</span>
              </h3>
              <div className='checkbox-content' style={{ whiteSpace: 'normal' }}>
                {i18n.t('MemberScreen.BlockUserText')}
              </div>
            </div>
            <br></br>
          </>
        )}
        {isVineProfile ? (
          <VineProfileActions user={user} onDismiss={onDismiss} />
        ) : (
          <div
            className='button-container'
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            {launchOneOnOneWithFriend && (
              <DefaultButton styles={buttonStyles} onClick={handleCommunicate}>
                {i18n.t('Communicate')}
              </DefaultButton>
            )}
            {shareEnabled && (
              <DefaultButton styles={buttonStyles} onClick={handleShare}>
                {i18n.t('Share')}
              </DefaultButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const UserProfileDialog: React.FunctionComponent<IUserProfileDialogProps> = ({
  user,
  dismiss,
  launchOneOnOneWithFriend,
  hideUniqueId
}) => {
  return (
    <Dialog
      hidden={false}
      onDismiss={() => {
        dismiss(undefined);
      }}
      dialogContentProps={{
        showCloseButton: true
      }}
      modalProps={{
        isBlocking: false,
        styles: { main: { borderRadius: '20px' } }
      }}
    >
      <UserProfile
        user={user}
        onDismiss={() => {
          dismiss(undefined);
        }}
        launchOneOnOneWithFriend={launchOneOnOneWithFriend}
        hideUniqueId={hideUniqueId}
      />
    </Dialog>
  );
};
